"use client";

/**
 * "Why BB" in 16:9, told with the classic four-part pitch (Gordon 2026-11-03,
 * after a keynote slide: grab attention → show the problem → the product fixes
 * it → finish with the payoff), using BB's own content: every release cover,
 * the people on BB, real app screens and the per-stream rates /launch uses
 * (`src/data/stream-rates.ts`), so the numbers never drift from the deck.
 *
 * One stage, sized by its width (container query units), so it reads the
 * same in the page, full screen on a projector or captured as a video. Plays
 * on a loop; space = play/pause, ←/→ = beats, F = full screen. The chapter
 * strip at the bottom is the formula itself and jumps between chapters.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { getVisibleReleases } from "@/services/home";
import { STREAM_RATES } from "@/data/stream-rates";
import { PLATFORM_FEE_RATE } from "@/lib/platform-fee";
import { directSaleEstimate, royaltyUnitsToEqual } from "@/lib/royalty-calculator";
import { niceStep, snap, useDisplayCurrency } from "@/components/currency/display-currency";
import { PlatformLogo, hasPlatformLogo } from "@/components/icons/platform-logos";
import { BBSignature } from "@/components/home/bb-signature";
import { cn } from "@/lib/utils";
import { StoryFrame } from "./story-frame";

// Stripe's standard EEA card pricing, as on /launch.
const CARD_PERCENT = 1.5;
const CARD_FIXED_EUR = 0.25;

type BeatId = "question" | "count" | "perStream" | "problems" | "app" | "pills" | "net" | "end";
const BEATS: { id: BeatId; chapter: 0 | 1 | 2 | 3; ms: number }[] = [
  { id: "question", chapter: 0, ms: 5000 },
  { id: "count", chapter: 0, ms: 6500 },
  { id: "perStream", chapter: 1, ms: 7500 },
  { id: "problems", chapter: 1, ms: 7000 },
  { id: "app", chapter: 2, ms: 7500 },
  { id: "pills", chapter: 2, ms: 8000 },
  { id: "net", chapter: 3, ms: 7000 },
  { id: "end", chapter: 3, ms: 9000 },
];
const CHAPTERS = ["chapter1", "chapter2", "chapter3", "chapter4"] as const;
const PER_STREAM = ["apple", "youtube_music", "spotify", "tiktok"] as const;
const APP_SCREENS = ["/images/hero-tips/release.jpg", "/images/hero-tips/marketplace.jpg", "/images/hero-tips/sync.jpg", "/images/hero-tips/news.jpg"];
const FADE_MS = 700;

function shuffled<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Counts up to `to` once mounted (restarts when its beat restarts). */
function CountUp({ to, ms = 2200, delay = 0, format }: { to: number; ms?: number; delay?: number; format: (n: number) => string }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setV(to); return; }
    let raf = 0;
    const start = performance.now() + delay;
    const tick = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - start) / ms));
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, ms, delay]);
  return <>{format(v)}</>;
}

/**
 * `kiosk` (the /screens player): no controls or keys, and instead of looping it
 * calls `onDone` after the last beat so the player moves on.
 */
export function WhyStory({ kiosk = false, onDone }: { kiosk?: boolean; onDone?: () => void } = {}) {
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const t = useTranslations("whyStory");
  const tIntro = useTranslations("home.intro");
  const locale = useLocale();
  const cur = useDisplayCurrency();

  // ── Content ────────────────────────────────────────────────────────────────
  const [covers, setCovers] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    getVisibleReleases().then(list => { if (alive) setCovers(shuffled(list.map(r => r.imageUrl).filter(Boolean)).slice(0, 36)); }).catch(() => {});
    fetch("/api/launch/people").then(r => r.json()).then((d: { founders?: string[]; people?: string[] }) => {
      if (alive) setPeople(shuffled([...(d.founders || []), ...(d.people || [])]).slice(0, 24));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── Numbers (the visitor's own currency, ~100 kr worth) ────────────────────
  const nums = useMemo(() => {
    const step = niceStep(100 * cur.perSek, 10);
    const price = snap(100 * cur.perSek, step);
    const calc = directSaleEstimate(Math.round(price * 100), PLATFORM_FEE_RATE, CARD_PERCENT, Math.round(CARD_FIXED_EUR * cur.perEur * 100));
    const spotify = STREAM_RATES.find(r => r.key === "spotify")!;
    const streams = royaltyUnitsToEqual(calc.netCents, (spotify.usd ?? 0.0033) * cur.perUsd) ?? 0;
    const perStream = PER_STREAM.map(key => {
      const r = STREAM_RATES.find(x => x.key === key)!;
      return { key, label: r.label, unit: r.unit, amount: (r.usd ?? 0) * cur.perUsd };
    });
    return { price, net: calc.netCents / 100, streams, perStream };
  }, [cur.perSek, cur.perEur, cur.perUsd]);
  const money = (n: number) => cur.money(n);
  // Round prices without öre/cents ("100 kr", not "100,00 kr").
  const price = (n: number) => Number.isInteger(n)
    ? new Intl.NumberFormat(locale, { style: "currency", currency: cur.code, maximumFractionDigits: 0 }).format(n)
    : cur.money(n);
  const tiny = (n: number) => new Intl.NumberFormat(locale, { style: "currency", currency: cur.code, maximumSignificantDigits: 2 }).format(n);
  const whole = (n: number) => new Intl.NumberFormat(locale).format(Math.round(n));

  // ── Clock ──────────────────────────────────────────────────────────────────
  const [beat, setBeat] = useState(0);
  const [round, setRound] = useState(0); // bumps on every (re)start of a beat → restarts its CSS
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0); // 0–1 inside the current beat
  const elapsed = useRef(0);
  const goTo = useCallback((i: number) => {
    elapsed.current = 0;
    setProgress(0);
    setBeat(((i % BEATS.length) + BEATS.length) % BEATS.length);
    setRound(r => r + 1);
  }, []);
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      elapsed.current += now - last;
      last = now;
      const len = BEATS[beat].ms;
      if (elapsed.current >= len) {
        if (kiosk && beat === BEATS.length - 1 && doneRef.current) { window.clearInterval(id); doneRef.current(); }
        else goTo(beat + 1);
      }
      else setProgress(elapsed.current / len);
    }, 80);
    return () => window.clearInterval(id);
  }, [playing, beat, goTo, kiosk]);

  const b = BEATS[beat];
  const leaving = progress * b.ms > b.ms - FADE_MS;
  const rise = (ms: number) => ({ animationDelay: `${ms}ms` });

  // ── Beats ──────────────────────────────────────────────────────────────────
  const scene = (() => {
    switch (b.id) {
      case "question":
        return (
          <div className="space-y-[2cqw]">
            <p className="bb-intro-pop text-[1.6cqw] font-semibold uppercase tracking-[0.3em] opacity-70" style={rise(100)}>{t("chapter1")}</p>
            <p className="bb-intro-rise bb-impact text-[7.4cqw] leading-[1.02] max-w-[70cqw]" style={rise(350)}>{t("question")}</p>
          </div>
        );
      case "count":
        return (
          <div className="space-y-[1.4cqw]">
            <p className="bb-intro-rise text-[4.4cqw] font-bold leading-tight" style={rise(100)}>{t("onePurchase", { price: price(nums.price) })}</p>
            <p className="bb-intro-rise bb-impact text-[13cqw] leading-none tabular-nums" style={rise(400)}>
              = <CountUp to={nums.streams} delay={500} format={whole} />
            </p>
            <p className="bb-intro-rise flex items-center gap-[1.2cqw] text-[3.6cqw] font-bold leading-tight" style={rise(900)}>
              {hasPlatformLogo("spotify") && <PlatformLogo id="spotify" decorative className="h-[4cqw] w-[4cqw]" />}
              {t("streamsToEarn", { platform: "Spotify" })}
            </p>
          </div>
        );
      case "perStream": {
        const max = Math.max(...nums.perStream.map(p => p.amount));
        return (
          <div className="w-full max-w-[78cqw] space-y-[2cqw]">
            <p className="bb-intro-rise bb-impact text-[5.6cqw] leading-none" style={rise(100)}>{t("perStreamTitle")}</p>
            <ul className="space-y-[1.1cqw]">
              {nums.perStream.map((p, i) => (
                <li key={p.key} className="bb-intro-pop grid grid-cols-[24cqw_1fr_16cqw] items-center gap-[1.4cqw] text-[2.6cqw]" style={rise(500 + i * 350)}>
                  <span className="flex items-center gap-[0.8cqw] font-semibold">
                    {hasPlatformLogo(p.key) && <PlatformLogo id={p.key} decorative className="h-[2.4cqw] w-[2.4cqw]" />}
                    {p.key === "youtube_music" ? "YouTube" : p.label}
                  </span>
                  <span className="h-[1.6cqw] overflow-hidden rounded-full bg-[#f5f1ea]/15">
                    <span className="ws-grow block h-full rounded-full bg-[#f5f1ea]" style={{ width: `${Math.max(0.6, (p.amount / max) * 100)}%`, animationDelay: `${700 + i * 350}ms` }} />
                  </span>
                  <span className="text-right font-semibold tabular-nums">{tiny(p.amount)}</span>
                </li>
              ))}
            </ul>
            <p className="bb-intro-pop text-[1.6cqw] opacity-75" style={rise(2200)}>{t("perStreamNote")}</p>
          </div>
        );
      }
      case "problems":
        return (
          <ol className="space-y-[1.6cqw]">
            {(["problem1", "problem2", "problem3"] as const).map((k, i) => (
              <li key={k} className="bb-intro-rise flex items-baseline gap-[2cqw]" style={rise(200 + i * 900)}>
                <span className="text-[1.6cqw] font-semibold tracking-[0.2em] opacity-60">0{i + 1}</span>
                <span className="bb-impact text-[5.6cqw] leading-[1.05]">{t(k)}</span>
              </li>
            ))}
          </ol>
        );
      case "app":
        return (
          <div className="grid w-full grid-cols-[1fr_1.15fr] items-center gap-[4cqw]">
            <div className="space-y-[1.6cqw]">
              <p className="bb-intro-pop text-[1.6cqw] font-semibold uppercase tracking-[0.3em] opacity-70" style={rise(100)}>Breakin&apos; Bread</p>
              <p className="bb-intro-rise bb-impact text-[6cqw] leading-[1.02]" style={rise(300)}>{t("fixTitle")}</p>
            </div>
            <div className="grid grid-cols-2 gap-[1.4cqw]">
              {APP_SCREENS.map((src, i) => (
                <div
                  key={src}
                  className="ws-screen overflow-hidden rounded-[0.7cqw] border border-[#f5f1ea]/30 bg-black shadow-2xl"
                  style={{ animationDelay: `${400 + i * 550}ms`, ["--ws-tilt" as string]: `${i % 2 === 0 ? -1.5 : 1.5}deg` }}
                >
                  <div className="flex h-[1.4cqw] items-center gap-[0.35cqw] bg-[#f5f1ea]/10 px-[0.7cqw]">
                    {[0, 1, 2].map(d => <span key={d} className="h-[0.5cqw] w-[0.5cqw] rounded-full bg-[#f5f1ea]/40" />)}
                  </div>
                  <div className="relative aspect-[16/10]">
                    <Image src={src} alt="" fill sizes="25vw" className="object-cover object-top" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case "pills":
        return (
          <div className="space-y-[2.4cqw]">
            <ul className="flex max-w-[80cqw] flex-wrap gap-[1.2cqw]">
              {(["fix1", "fix2", "fix3", "fix4", "fix5"] as const).map((k, i) => (
                <li key={k} className="bb-intro-pop rounded-full bg-[#f5f1ea] px-[1.8cqw] py-[0.8cqw] text-[2.7cqw] font-semibold text-[#171512]" style={rise(200 + i * 450)}>
                  {t(k)}
                </li>
              ))}
            </ul>
            <p className="bb-intro-rise bb-impact text-[4.6cqw] leading-[1.05]" style={rise(2700)}>
              {/* .bb-impact is upper case, which turns π into Π. */}
              {t("fee").split("π").map((part, i, all) => (
                <Fragment key={i}>{part}{i < all.length - 1 && <span className="normal-case">π</span>}</Fragment>
              ))}
            </p>
          </div>
        );
      case "net":
        return (
          <div className="space-y-[1.2cqw]">
            <p className="bb-intro-rise text-[4.4cqw] font-bold leading-tight" style={rise(100)}>{t("onePurchase", { price: price(nums.price) })}</p>
            <p className="bb-intro-rise text-[3.6cqw] font-bold leading-tight" style={rise(400)}>{t("youGet")}</p>
            <p className="bb-intro-rise bb-impact text-[13cqw] leading-none tabular-nums" style={rise(600)}>
              <CountUp to={nums.net} delay={700} ms={1800} format={money} />
            </p>
            <p className="bb-intro-pop text-[2.2cqw] font-semibold opacity-85" style={rise(1600)}>{t("afterFees")}</p>
          </div>
        );
      case "end":
        return (
          <div className="flex h-full w-full items-end justify-between gap-[3cqw]">
            <div className="space-y-[1.2cqw]">
              {(["own1", "own2", "own3"] as const).map((k, i) => (
                <p key={k} className="bb-intro-rise bb-impact text-[5.4cqw] leading-[1.02]" style={rise(200 + i * 700)}>{t(k)}</p>
              ))}
              <p className="bb-intro-pop pt-[1.2cqw] text-[2.2cqw] font-semibold" style={rise(2800)}>
                {tIntro("byMakers")}, {tIntro("forMakers")}
              </p>
              <p className="bb-intro-pop text-[2.2cqw] font-semibold tracking-wide opacity-80" style={rise(3300)}>breakinbread.com</p>
            </div>
            <div className="bb-intro-pop shrink-0 text-[#f5f1ea]" style={rise(3000)}>
              <BBSignature className="w-[9cqw] bg-[#f5f1ea]" />
            </div>
          </div>
        );
    }
  })();

  // Background per beat: the cover wall for the hook, people at the end.
  const wallRows = covers.length > 0 ? [0, 1, 2].map(r => covers.filter((_, i) => i % 3 === r)) : [];
  // BB's artwork behind every page, in colour (Gordon 2026-11-03: "use the BB
  // artwork on all pages … a little colour with the help of random artwork").
  const showWall = b.id !== "end";
  const showPeople = b.id === "end" && people.length > 0;

  return (
    <StoryFrame
      kiosk={kiosk}
      label={t("title")}
      playing={playing}
      onTogglePlay={() => setPlaying(p => !p)}
      onPrev={() => goTo(beat - 1)}
      onNext={() => goTo(beat + 1)}
      onRestart={() => { goTo(0); setPlaying(true); }}
    >
      <style>{`
        @keyframes ws-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .ws-grow { transform-origin: left; animation: ws-grow 1.4s cubic-bezier(0.22,0.61,0.36,1) both; }
        @keyframes ws-screen { from { opacity: 0; transform: translateY(6%) rotate(0deg) scale(0.94); } to { opacity: 1; transform: rotate(var(--ws-tilt)) scale(1); } }
        .ws-screen { animation: ws-screen 1.3s cubic-bezier(0.22,0.61,0.36,1) both; }
        @keyframes ws-face { from { opacity: 0; transform: scale(0.85); } to { opacity: 0.55; transform: scale(1); } }
        .ws-face { animation: ws-face 1.2s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .ws-grow, .ws-screen, .ws-face { animation-duration: 0.01s; } }
      `}</style>

        {/* Background layers */}
        {wallRows.length > 0 && (
          // Three rows fill the whole stage, top to bottom (Gordon 2026-11-03).
          <div aria-hidden className={cn("absolute inset-0 flex flex-col gap-[0.8cqw] py-[0.8cqw] transition-opacity duration-1000", showWall ? "opacity-100" : "opacity-0")}>
            {wallRows.map((row, r) => (
              <div key={r} className="min-h-0 flex-1 overflow-hidden">
                <div className={cn("flex h-full w-max gap-[0.8cqw] bb-intro-drift", r === 1 && "bb-intro-drift-reverse")}>
                  {[...row, ...row].map((src, i) => (
                    <div key={i} className="relative aspect-square h-full shrink-0 overflow-hidden rounded-[0.6cqw]">
                      <Image src={src} alt="" fill sizes="20vw" className="object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {/* Darker where the words are, so they always read. */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#171512]/95 via-[#171512]/78 to-[#171512]/50" />
          </div>
        )}
        {showPeople && (
          // Square tiles, like record sleeves (Gordon 2026-11-03: "always square
          // images in the background, music artwork is always square").
          <div aria-hidden key={`faces-${round}`} className="absolute inset-0 grid grid-cols-8 content-center gap-[0.6cqw] overflow-hidden p-[0.6cqw]">
            {Array.from({ length: 40 }, (_, i) => people[i % people.length]).map((src, i) => (
              <div key={i} className="ws-face relative aspect-square overflow-hidden rounded-[0.6cqw]" style={{ animationDelay: `${(i % 8) * 120 + Math.floor(i / 8) * 180}ms` }}>
                <Image src={src} alt="" fill sizes="13vw" className="object-cover" />
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#171512] via-[#171512]/80 to-[#171512]/35" />
          </div>
        )}

        {/* The beat */}
        <div
          key={`${beat}-${round}`}
          className={cn("absolute inset-0 flex items-center px-[6cqw] pb-[9cqw] pt-[5cqw] transition-opacity duration-700", leaving ? "opacity-0" : "opacity-100")}
          aria-live="polite"
        >
          {scene}
        </div>

        {/* The formula: WHY BB → four chapters, the current one lit. */}
        <nav aria-label={t("chapters")} className="absolute inset-x-[6cqw] bottom-[2.4cqw] z-10 flex items-center gap-[1.4cqw]">
          <span className="shrink-0 rounded-full border border-[#f5f1ea]/60 px-[1.2cqw] py-[0.45cqw] text-[1.1cqw] font-bold uppercase tracking-[0.2em]">{t("title")}</span>
          <ol className="relative grid flex-1 grid-cols-4 gap-[1cqw]">
            {CHAPTERS.map((k, i) => {
              const first = BEATS.findIndex(x => x.chapter === i);
              const beats = BEATS.filter(x => x.chapter === i);
              const done = b.chapter > i;
              const active = b.chapter === i;
              const inChapter = active ? (BEATS.slice(first, beat).reduce((s, x) => s + x.ms, 0) + progress * b.ms) / beats.reduce((s, x) => s + x.ms, 0) : done ? 1 : 0;
              return (
                <li key={k}>
                  <button
                    type="button"
                    onClick={() => goTo(first)}
                    aria-current={active ? "step" : undefined}
                    className={cn("w-full text-left transition-opacity", active ? "opacity-100" : "opacity-55 hover:opacity-90")}
                  >
                    <span className="block h-[0.3cqw] overflow-hidden rounded-full bg-[#f5f1ea]/20">
                      <span className="block h-full origin-left bg-[#f5f1ea]" style={{ transform: `scaleX(${inChapter})` }} />
                    </span>
                    <span className="mt-[0.5cqw] flex items-baseline gap-[0.6cqw] text-[1.1cqw] font-semibold">
                      <span className="tabular-nums opacity-70">0{i + 1}</span>
                      <span className="truncate">{t(k)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
    </StoryFrame>
  );
}
